import { useEffect, useState } from "react";
import { ChatWindow } from "./components/ChatWindow";
import { CopilotSidebar } from "./components/CopilotSidebar";
import { KnowledgeBase } from "./components/KnowledgeBase";
import { useAutopilot } from "./hooks/useAutopilot";
import { useCopilot } from "./hooks/useCopilot";
import { api } from "./lib/api";

type View = "console" | "knowledge";

const SESSION_KEY = "copilot.session_id";
const AUTOPILOT_KEY = "copilot.autopilot";

export default function App() {
  const [view, setView] = useState<View>("console");
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  // Per tab, not per user: an autopilot left on by accident must not outlive the session.
  const [autopilot, setAutopilot] = useState(
    () => window.sessionStorage.getItem(AUTOPILOT_KEY) === "1",
  );

  useEffect(() => {
    window.sessionStorage.setItem(AUTOPILOT_KEY, autopilot ? "1" : "0");
  }, [autopilot]);

  // The session id is the LangGraph thread id. Keeping it in sessionStorage means a page
  // refresh resumes the same conversation from the checkpointer instead of starting over.
  useEffect(() => {
    const existing = window.sessionStorage.getItem(SESSION_KEY);
    if (existing) {
      setSessionId(existing);
      return;
    }
    api
      .createSession()
      .then(({ session_id }) => {
        window.sessionStorage.setItem(SESSION_KEY, session_id);
        setSessionId(session_id);
      })
      .catch(() => setSessionId(`sess-local-${Date.now()}`));
  }, []);

  const { connection, transcript, guidance, activeNodes, thinking, error, send } =
    useCopilot(sessionId);

  // Clear the composer when autopilot sends, so a half-typed reply is not left looking
  // like it is still queued behind the one that just went out.
  useAutopilot({
    enabled: autopilot,
    guidance,
    transcript,
    send,
    onSent: () => setDraft(""),
  });

  const newConversation = () => {
    window.sessionStorage.removeItem(SESSION_KEY);
    window.location.reload();
  };

  return (
    <div className="app">
      <header className="topbar">
        <h1>Support Agent Copilot</h1>
        <nav className="tabs">
          <button
            className={`tab ${view === "console" ? "active" : ""}`}
            onClick={() => setView("console")}
          >
            Console
          </button>
          <button
            className={`tab ${view === "knowledge" ? "active" : ""}`}
            onClick={() => setView("knowledge")}
          >
            Knowledge
          </button>
        </nav>
        <div className="spacer" />
        <span className="meta">{sessionId ?? "starting…"}</span>
        <button className="ghost" onClick={newConversation}>New conversation</button>
        <div className="status">
          <span className={`dot ${connection}`} />
          {connection}
        </div>
      </header>

      {view === "console" ? (
        <div className="console">
          <ChatWindow
            transcript={transcript}
            onSend={send}
            draft={draft}
            onDraftChange={setDraft}
            disabled={connection !== "open"}
            thinking={thinking}
            autopilot={autopilot}
            onAutopilotChange={setAutopilot}
          />
          <CopilotSidebar
            guidance={guidance}
            activeNodes={activeNodes}
            thinking={thinking}
            error={error}
            onUseSuggestion={setDraft}
          />
        </div>
      ) : (
        <KnowledgeBase />
      )}
    </div>
  );
}
